import { RedirectCommand, UrlTree } from '@angular/router';

type GuardResult = boolean | UrlTree | RedirectCommand;

/**
 * Logs the decision a guard made and returns it unchanged, so a single
 * `return logGuardResult('fooGuard', result, context)` at every exit point
 * doubles as the guard's return statement. Without this, the console only
 * shows that a guard ran (`console.debug('fooGuard')`), not what it decided
 * or why — which makes redirect loops across `/iirds/*` guards very hard to
 * diagnose.
 *
 * @param guardName name of the guard, used to group log lines (e.g. via the
 *   browser console filter `[guard:packageGuard]`)
 * @param result the value the guard is about to return
 * @param context free-form details explaining *why* this decision was made
 *   (e.g. the param values inspected, the store state read)
 */
export function logGuardResult<T extends GuardResult>(
  guardName: string,
  result: T,
  context?: Record<string, unknown>
): T {
  const decision = describeGuardResult(result);
  const style = result === false ? 'color: #e33' : 'color: inherit';
  console.debug(`%c[guard:${guardName}] ${decision}`, style, context ?? {});
  return result;
}

function describeGuardResult(result: GuardResult): string {
  if (result === true) {
    return 'ALLOW';
  }
  if (result === false) {
    return 'DENY';
  }
  const tree = result instanceof RedirectCommand ? result.redirectTo : result;
  return `REDIRECT -> ${tree.toString()}`;
}
